import { dismissToasts } from '@pypestream/design-system';

import { tableStore } from '../stores/table-store';
import { sendUserMessage } from '../xstate/app.xstate-utils';

export const PUBLISH_IN_PROGRESS_TOAST_ID = 'publish-in-progress';

/** Status values for solution version (from backend). */
export type SolutionVersionStatus = 'ACTIVE' | 'PUBLISHING' | 'CREATING';

/**
 * Publish attempts whose outcome has already been announced in this tab.
 *
 * Keyed by the `tenancy_solution_deployments` row id — one row per attempt — so a republish,
 * a retry after a failure, and a second publish of the same version each announce again,
 * while the several subscribers that can observe the same attempt announce it only once.
 * Attempts with no deployment row fall back to a version-scoped key (see
 * `buildPublishAttemptId`).
 */
const announcedPublishAttemptIds = new Set<string>();

/**
 * Restores UI state after a publish attempt resolves: the Publish button lock lifted and
 * the in-progress toast gone. Idempotent — safe to call from multiple subscribers.
 *
 * It does not touch the table mode. Under draft-only versioning a publish never puts the
 * editor into view mode, so there is nothing to restore — and forcing `editing` here
 * would override whatever mode the table is legitimately in (loading, a viewer's
 * read-only table).
 */
export function restorePublishUiState(): void {
  tableStore.setSolutionVersionPublishingInProgress(false);
  dismissToasts(PUBLISH_IN_PROGRESS_TOAST_ID);
}

/**
 * The dedup key for one publish attempt: the deployment row id when there is one, otherwise
 * the caller's fallback — the solution plus the lock's `publishStartedAt`, which is what
 * tells one attempt from the next when the deployment rows are unavailable.
 */
export function buildPublishAttemptId({
  deploymentId,
  fallbackKey,
}: {
  deploymentId?: string | null;
  fallbackKey?: string | null;
}): string | undefined {
  if (deploymentId) return deploymentId;
  return fallbackKey ? `attempt:${fallbackKey}` : undefined;
}

/**
 * Announces how a publish ended and lifts the Publish button lock.
 *
 * UI state is restored unconditionally — even for an attempt already announced, nobody may
 * be left with a disabled button behind a stale in-progress toast — while the toast itself
 * is shown once per attempt.
 *
 * A failed publish gets a toast that does not auto-close: it is the one thing the user has
 * to act on, and before deployment rows existed a coworker was never told at all.
 */
export function notifyPublishOutcome({
  attemptId,
  outcome,
  message,
}: {
  attemptId?: string;
  outcome: 'success' | 'failure';
  message: string;
}): void {
  restorePublishUiState();

  if (!attemptId || announcedPublishAttemptIds.has(attemptId)) return;
  announcedPublishAttemptIds.add(attemptId);

  sendUserMessage({
    type: outcome === 'success' ? 'success' : 'error',
    text: message,
    autoClose: outcome === 'success' ? 5000 : false,
  });
}
